import type { RemovableRef} from '@vueuse/core';
import { StorageSerializers, useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import type { SystemSetting } from '@/models/entities/SystemSetting';
import type { CurrencyOptions } from '@/models/common/CurrencyOptions';
import type { RegressionOptions } from '@/models/common/RegressionOptions';
import { usePayAuditStore } from '@/store/payAuditStore';
import { usePayAuditEntityStore } from '@/store/salarySurvey/payAuditEntityStore';
import { useDiscussionStore } from '@/views/discussions/store/discussionStore';
import { useSettings } from '@/login/composables/useSettings';

type RowGroupState = {
    key: string,
    opened: string[]
};

type SystemState = {
    _isSimulatingUser: boolean;
    _rowGroupStates: RemovableRef<RowGroupState[]>;
    _allowNonDeclaredGenderAnalysis: RemovableRef<boolean>;
    _allowEtnicity: RemovableRef<boolean>;
    _baseRegistryOrgNodeEmploymentsOnlyActive: RemovableRef<boolean>;
    _allowAutomatedAnalysis: RemovableRef<string[]>;
    _allowCorrelationAnalysis: RemovableRef<string[]>;
    _showBenchmark: RemovableRef<boolean>;
    _usesPerformance: RemovableRef<boolean>;
    _currencyOptions: RemovableRef<CurrencyOptions>;
    _systemSettings: RemovableRef<Partial<SystemSetting>>;
    _regressionOptions: RemovableRef<RegressionOptions>;
};

export const useSystemStore = defineStore('system', {
    state: (): SystemState => (
        {
            _isSimulatingUser: false,
            _rowGroupStates: useLocalStorage<RowGroupState[]>('rowGroupStates', [], { serializer: StorageSerializers.object }),
            _allowNonDeclaredGenderAnalysis: useLocalStorage<boolean>('allowNonDeclaredGenderAnalysis', false, { serializer: StorageSerializers.boolean }),
            _allowEtnicity: useLocalStorage<boolean>('allowEthnicity', false, { serializer: StorageSerializers.boolean }),
            _baseRegistryOrgNodeEmploymentsOnlyActive: useLocalStorage<boolean>('baseRegistryOrgNodeEmploymentsOnlyActive', false, { serializer: StorageSerializers.boolean }),
            _allowAutomatedAnalysis: useLocalStorage<string[]>('allowAutomatedAnalysis', [], { serializer: StorageSerializers.object }),
            _showBenchmark: useLocalStorage<boolean>('showBenchmark', false, { serializer: StorageSerializers.boolean }),
            _usesPerformance: useLocalStorage<boolean>('usesPerformance', false, { serializer: StorageSerializers.boolean }),
            _allowCorrelationAnalysis: useLocalStorage<string[]>('allowCorrelationAnalysis', [], { serializer: StorageSerializers.object }),
            _systemSettings: useLocalStorage<Partial<SystemSetting>>('systemSettings', {}, { serializer: StorageSerializers.object }),
            _currencyOptions: useLocalStorage<CurrencyOptions>('currencyOptions',
                {
                    defaultCurrency: '',
                    hasCompaRatio: false,
                    currencyTypes: [],
                },
                {
                    serializer: StorageSerializers.object
                }
            ),
            _regressionOptions: useLocalStorage<RegressionOptions>('regressionOptions',
                {
                    showLinearRegression: false,
                    showR: false,
                    showR2: false,
                    showEQ: false,
                    showSE: false
                },
                {
                    serializer: StorageSerializers.object
                }
            ),
        }),
    getters: {
        rowGroupStates: state => state._rowGroupStates,
        rowGroupState: state => (key: string): RowGroupState => {
            let item = state._rowGroupStates.find(c => c.key === key);

            if (item == null) {
                item = {
                    key,
                    opened: []
                };

                state._rowGroupStates.push(item);
            }

            return item;
        },
        getIsSimulatingUser: state => state._isSimulatingUser,
        allowNonDeclaredGenderAnalysis: state => state._allowNonDeclaredGenderAnalysis,
        allowEthnicity: state => state._allowEtnicity,
        baseRegistryOrgNodeEmploymentsOnlyActive: state => state._baseRegistryOrgNodeEmploymentsOnlyActive,
        showBenchmark: state => state._showBenchmark,
        usesPerformance: state => state._usesPerformance,
        allowAutomatedAnalysis: state => (id: string): boolean => state._allowAutomatedAnalysis.some(x => x === id),
        allowAutomatedAnalysisList: state => state._allowAutomatedAnalysis,
        allowCorrelationAnalysis: state => (id: string): boolean => state._allowCorrelationAnalysis.some(x => x === id),
        allowCorrelationAnalysisList: state => state._allowCorrelationAnalysis,
        systemSettings: state => state._systemSettings,
        currencyOptions: state => state._currencyOptions,
        regressionOptions: state => state._regressionOptions
    },
    actions: {
        // save ag-grid group open state
        setRowGroupIsOpened(key: string, group: string, open: boolean): void {
            const item = this._rowGroupStates.find(c => c.key === key);
            if (item == null) return;

            if (open) {
                item.opened.push(group);
            } else {
                item.opened.splice(item.opened.findIndex(i => i === group), 1);
            }
        },
        // load ag-grid group open state
        getRowGroupIsOpened(key: string, group: string): boolean {
            let item = this._rowGroupStates.find(c => c.key === key);

            if (item == null) {
                item = {
                    key,
                    opened: []
                };

                this._rowGroupStates.push(item);
            }

            return item.opened.some(c => c === group);
        },
        /** is the user simulated? */
        setIsSimulatingUser(state: boolean) {
            this._isSimulatingUser = state;
        },
        setSystemSettings(value: Partial<SystemSetting>): void {
            this._systemSettings = value;
        },
        setAllowAutomatedAnalysis(value: string): void {
            const index = this._allowAutomatedAnalysis.indexOf(value);

            if (index < 0) {
                this._allowAutomatedAnalysis.push(value);
            } else {
                this._allowAutomatedAnalysis.splice(index, 1);
            }
        },
        setAllowCorrelationAnalysis(value: string): void {
            const index = this._allowCorrelationAnalysis.indexOf(value);

            if (index < 0) {
                this._allowCorrelationAnalysis.push(value);
            } else {
                this._allowCorrelationAnalysis.splice(index, 1);
            }
        },
        setBaseRegistryOrgNodeEmploymentsOnlyActive(value: boolean): void {
            this._baseRegistryOrgNodeEmploymentsOnlyActive = value;
        },
        setAllowNonDeclaredGenderAnalysis(value: boolean) {
            this._allowNonDeclaredGenderAnalysis = value;
        },
        setAllowEthnicity(value: boolean): void {
            this._allowEtnicity = value;
        },
        setShowBenchmark(value: boolean): void {
            this._showBenchmark = value;
        },
        setUsesPerformance(value: boolean): void {
            this._usesPerformance = value;
        },
        updateRegressionOptions(propertyName: keyof RegressionOptions, value: boolean): void {
            this._regressionOptions[propertyName] = value;
        },
        resetLoggedInInstanceStorage(): void {
            this._systemSettings = {};
        },
        resetEntityDataStorage(): void {
            usePayAuditStore().$reset();
            usePayAuditEntityStore().$reset();
            useDiscussionStore().$reset();
        },
        setInstanceSettings() {
            try {
                // Set allow ethnicity
                const { instanceSettings } = useSettings();
                this._allowEtnicity = instanceSettings.value?.AllowAnalysisOfEthnicity ?? false;
                // We set this temporarily to allowEthnicity since only those users use this function.
                // This needs to have it's own system setting later on and then we can just use that instead.
                this._allowNonDeclaredGenderAnalysis = this._allowEtnicity;
            } catch {
                this._allowEtnicity = false;
                this._allowNonDeclaredGenderAnalysis = false;
            }
        },
        setCurrencyOptions(value: CurrencyOptions): void {
            value.defaultCurrency = value.currencyTypes.length > 1
                ? ''
                : value.currencyTypes[0];

            this._currencyOptions = value;
        }
    }
});
